import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson, type PostValue } from './client'

/**
 * Settings -> Authentication, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Authentication.php`:
 *
 *   index        -> GET  /settings/authentication
 *   offices/{id} -> GET  /settings/authentication/offices/{company}
 *   save         -> POST /settings/authentication/providers
 *   remove/{id}  -> POST /settings/authentication/providers/{id}/remove
 *   readMetadata -> POST /settings/authentication/metadata
 *   groups/{id}  -> GET  /settings/authentication/providers/{id}/groups
 *   saveGroup    -> POST /settings/authentication/providers/{id}/groups
 *   removeGroup  -> POST /settings/authentication/providers/{id}/groups/{rule}/remove
 *   (nou)        -> POST /settings/authentication/providers/{id}/sync?dry=1          Preview sync
 *   (nou)        -> POST /settings/authentication/providers/{id}/groups/propose      reguli din rolurile noastre
 *   (nou)        -> POST /settings/authentication/providers/{id}/groups/import/preview   CSV, nescris
 *   (nou)        -> POST /settings/authentication/providers/{id}/groups/import       salvarea setului vazut
 *   (nou)        -> GET  /settings/authentication/events?before=&limit=     "Load more" la sign-in activity
 *   (nou)        -> GET  /settings/authentication/sync-runs?before=&limit=  "Load more" la directory sync
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * NICIUN SECRET NU VINE PE AICI. Un furnizor poarta trei steaguri - `hasSecret`,
 * `hasKey`, `hasBindPass` - si atat: ecranul spune DACA e ceva pastrat, ca sa
 * stie omul ca poate lasa casuta goala, dar nu arata niciodata ce.
 */

export type AuthProvider = {
  id: number
  protocol: 'oidc' | 'saml' | 'ldap' | string
  protocolName: string
  name: string
  buttonLabel: string
  issuer: string
  clientId: string
  scopes: string
  authorizeEndpoint: string
  idpCertificate: string
  spCertificate: string
  emailAttribute: string
  wantSignedAssertions: boolean
  ldapUri: string
  ldapStarttls: boolean
  ldapBaseDn: string
  ldapUserDnPattern: string
  ldapBindDn: string
  ldapUserFilter: string
  /** Certificatul controlerului se verifica (implicit da). */
  ldapTlsVerify?: boolean
  /** PEM-ul autoritatii proprii; gol = cele ale sistemului. */
  ldapCaCertificate?: string
  ldapSyncEnabled?: boolean
  ldapSyncBaseDn?: string
  ldapSyncFilter?: string
  /** Asignarea aparatelor din AD (stinsa implicit); optionale: un server mai vechi nu le trimite. */
  ldapAssignDevices?: boolean
  ldapComputerBaseDn?: string
  ldapComputerFilter?: string
  /** Pragurile asignarii dupa logari; lipsa = server mai vechi, se iau 3 / 5 / 30. */
  ldapAssignManagedBy?: boolean
  ldapAssignSuggestAfter?: number
  ldapAssignAutoAfter?: number
  ldapAssignWindowDays?: number
  ldapAssignIgnore?: string
  /**
   * Cum se ajunge la director: prin conectorul de la client (agentul Go) sau
   * direct, LDAPS de la noi. Il schimba numai administratorii de platforma;
   * lipsa = server mai vechi, adica `direct`.
   */
  ldapConnectionMode?: 'connector' | 'direct' | string
  /** Cod pe email dupa parola de domeniu (`require_email_code`); lipsa = stins. */
  requireEmailCode?: boolean
  /** `dn` = grupurile numai dupa DN; `cn` = si dupa numele scurt (M12). Lipsa = `cn`. */
  ldapGroupMatch?: 'dn' | 'cn'
  /** Numai raspunsuri semnate de un conector aprobat (K4); lipsa = stins. */
  ldapConnectorSigned?: boolean
  /** Modul direct cu IP: numele de pe certificat si amprenta SHA-256 - goale pentru cine nu e administrator de platforma. */
  ldapTlsPeerName?: string
  ldapTlsFingerprint?: string
  /** Conectorii firmei (modul conector), cu ultima comunicare. */
  connectors?: DirectoryConnector[]
  /** Alarma deschisa "niciun conector viu de 5 minute", daca e. */
  connectorAlarm?: { openedAt: string; lastSeen: string; message: string } | null
  companyId: number
  companyName: string
  officeId: number
  defaultRoleId: number
  roleName: string
  defaultAccessId: number
  groupsClaim: string
  groupsStrict: boolean
  provisioning: number
  provisioningLabel: string
  status: boolean
  /** Exista un secret pastrat? Nu si CARE. */
  hasSecret: boolean
  hasKey: boolean
  hasBindPass: boolean
  ruleCount: number
  /** IAM: firmele omului din grupuri (implicit stins); optionale - un server mai vechi nu le trimite. */
  iamCompanies?: boolean
  /** IAM: celelalte reguli potrivite devin roluri suplimentare (implicit stins). */
  iamExtraRoles?: boolean
  /** IAM: drepturile personale date de mana raman (implicit aprins). */
  iamKeepManual?: boolean
  /** Prefixul grupurilor propuse din rolurile noastre; gol = `OD-`. */
  iamRulePrefix?: string
  /** Etichetele rosii din lista, alese pe server dupa protocol. */
  warnings: string[]
}

export type AuthEvent = {
  id: number
  when: string
  label: string
  kind: string
  who: string
  provider: string
  detail: string
  from: string
}

export type Option = { id: number; name: string }

/** O rulare a sincronizarii cu directorul (`it_auth_sync_runs`). */
export type SyncRun = {
  id: number
  provider: string
  trigger: string
  status: 'ok' | 'partial' | 'failed' | string
  startedAt: string
  finishedAt: string
  seen: number
  created: number
  updated: number
  deactivated: number
  reactivated: number
  skipped: number
  errors: number
  /** Aparatele asignate din AD in rularea asta; lipsa = server fara asignare. */
  assigned?: number
  reassigned?: number
  ambiguous?: number
  skippedManual?: number
  /** Aparate cu o sugestie de stapan (intre pragul de sugestie si cel automat). */
  suggested?: number
  /** Oameni carora IAM-ul le-a schimbat (sau le-ar schimba) firmele / rolurile. */
  iamChanged?: number
  /** Numai la "Preview sync": ce s-ar schimba, om cu om. */
  preview?: string[]
  messages: string[]
}

/** "SSO obligatoriu pentru administratori", pe firma (`it_auth_policy`). */
export type AdminSsoPolicy = {
  adminSsoRequired: boolean
  breakGlassUserId: number
  /**
   * Nivelurile de acces care intra numai prin SSO (1, 2, 3, 6). null / lipsa =
   * regula veche: administratorii dupa rol si nivel (`adminSsoRequired`).
   */
  ssoRequiredLevels?: number[] | null
  /** Conturile locale ale firmei, din care se alege cel de urgenta. */
  accounts: Option[]
  hasActiveProvider: boolean
}

/** Un conector de director (agentul Go de la client), din `it_dir_connectors`. */
export type DirectoryConnector = {
  deviceId: number
  deviceName: string
  version: string
  baseDns: string[]
  lastSeen: string
  lastError: string
  lastSyncAt: string
  status: string
  /** Aprobat de un administrator (K4); '' = nu inca. Lipsa = server vechi. */
  approvedAt?: string
  /** Si-a inscris conectorul cheia de semnare? */
  hasKey?: boolean
}

export type AuthenticationPage = {
  canWrite: boolean
  /** Administrator de platforma (`it_platform_admins`): vede si schimba comutatorul conector / direct. */
  platformAdmin?: boolean
  callbackUrl: string
  acsUrl: string
  /** Adresa metadatelor NOASTRE; numarul furnizorului se lipeste la coada. */
  metadataUrl: string
  providers: AuthProvider[]
  companies: Option[]
  roles: Option[]
  events: AuthEvent[]
  /** Mai sunt evenimente dupa prima pagina ("Load more"); lipsa = server mai vechi, lista taiata. */
  eventsMore?: boolean
  provisioning: Array<{ value: number; label: string }>
  /** Extensia `ldap` exista pe server? Optionale: un server mai vechi nu le trimite. */
  ldapAvailable?: boolean
  policy?: AdminSsoPolicy
  syncRuns?: SyncRun[]
  /** Mai sunt rulari dupa prima pagina; lipsa = server mai vechi. */
  syncRunsMore?: boolean
}

export type AuthGroupRule = {
  id: number
  group: string
  roleId: number
  roleName: string
  order: number
  status: boolean
  /** Regula da toate firmele furnizorului; optionale - un server mai vechi nu le trimite. */
  allCompanies?: boolean
  companyIds?: number[]
  companyNames?: string[]
  /** Grupurile de tichete date de regula; optionale - un server mai vechi nu le trimite. */
  ticketGroupIds?: number[]
  ticketGroupNames?: string[]
}

export type AuthGroupsPage = {
  canWrite: boolean
  provider: {
    id: number
    name: string
    protocol?: string
    groupsClaim: string
    groupsStrict: boolean
    iamCompanies?: boolean
    iamExtraRoles?: boolean
    iamKeepManual?: boolean
    iamRulePrefix?: string
  }
  roles: Option[]
  /** Firmele furnizorului, din care se aleg firmele unei reguli. */
  companies?: Option[]
  /** Grupurile de tichete (departamentele) pe care le poate da o regula; lipsa = server mai vechi. */
  ticketGroups?: Option[]
  rules: AuthGroupRule[]
}

/** O regula propusa (din rolurile noastre) sau citita din CSV - nescrisa inca. */
export type ProposedRule = {
  group_value: string
  role_id: number
  all_companies: boolean
  company_ids: number[]
  company_names: string[]
  sort_order: number
  /** Cati oameni au azi rolul (numai la propunere). */
  users: number
  /** Exista deja o regula pentru grupul asta: salvarea o rescrie. */
  exists: boolean
  error: string
  /** Linia din fisier (numai la import). */
  line?: number
}

export type ProposedRules = { status: string; message: string; prefix?: string; rows: ProposedRule[] }

export type ImportResult = WriteResult & { added?: number; updated?: number; problems?: string[] }

/** Raspunsul scrierilor, ca in portal: `{status: 'OK' | 'ERROR', message}`. */
export type WriteResult = { status: string; message: string; id?: number }

export const authenticationQuery = queryOptions({
  queryKey: ['settings', 'authentication'],
  queryFn: () => apiGet<AuthenticationPage>('/settings/authentication'),
})

export function authGroupsQuery(providerId: number) {
  return queryOptions({
    queryKey: ['settings', 'authentication', 'groups', providerId],
    queryFn: () => apiGet<AuthGroupsPage>(`/settings/authentication/providers/${providerId}/groups`),
  })
}

export function officesQuery(companyId: number) {
  return queryOptions({
    queryKey: ['settings', 'authentication', 'offices', companyId],
    queryFn: () => apiGet<{ offices: Option[] }>(`/settings/authentication/offices/${companyId}`),
    enabled: companyId > 0,
  })
}

export function saveProvider(fields: Record<string, PostValue>) {
  return apiPost<WriteResult>('/settings/authentication/providers', fields)
}

export function removeProvider(id: number) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${id}/remove`, {})
}

export function readMetadata(source: string) {
  return apiPost<{
    status: string
    message: string
    fields?: { issuer: string; authorize_endpoint: string; idp_certificate: string }
  }>('/settings/authentication/metadata', { source })
}

export function saveGroupRule(providerId: number, fields: Record<string, PostValue>) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${providerId}/groups`, fields)
}

/** Scrie politica de SSO pentru administratori si contul de urgenta. */
export function saveAdminSsoPolicy(required: boolean, breakGlassUserId: number) {
  return apiPost<WriteResult>('/settings/authentication/policy', {
    admin_sso_required: required ? 1 : 0,
    break_glass_user_id: breakGlassUserId,
  })
}

/**
 * Scrie alegerea PE NIVEL DE ACCES: lista (si goala) trece politica de pe
 * regula veche pe nivel. Serverul tine `admin_sso_required` in pas.
 */
export function saveSsoRequiredLevels(levels: readonly number[], breakGlassUserId: number) {
  return apiPost<WriteResult>('/settings/authentication/policy', {
    sso_required_levels: [...levels].sort((a, b) => a - b).join(','),
    break_glass_user_id: breakGlassUserId,
  })
}

/**
 * "Load more": pagina urmatoare din jurnal. `before` = ID-ul ultimului rand
 * aratat (cursor - un eveniment nou nu muta randurile deja pe ecran).
 */
export function fetchAuthEvents(before: number, limit?: number) {
  const query = new URLSearchParams({ before: String(before) })
  if (limit) query.set('limit', String(limit))
  return apiGet<{ events: AuthEvent[]; more: boolean }>(`/settings/authentication/events?${query.toString()}`)
}

/** "Load more" la rularile sincronizarii cu directorul. */
export function fetchSyncRuns(before: number, limit?: number) {
  const query = new URLSearchParams({ before: String(before) })
  if (limit) query.set('limit', String(limit))
  return apiGet<{ syncRuns: SyncRun[]; more: boolean }>(`/settings/authentication/sync-runs?${query.toString()}`)
}

/** "Run now" - sincronizarea cu directorul pe un furnizor LDAP. */
export function runDirectorySync(providerId: number) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${providerId}/sync`, {})
}

/** "Test connection" pe modul conector: un ping si o cautare mica, prin agentul de la client. */
export function testDirectoryConnector(providerId: number) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${providerId}/connector-test`, {})
}

/**
 * "Remove" pe un conector tacut. 409 (ApiError) daca inca da semn - s-ar
 * inscrie singur inapoi la urmatorul poll; 404 daca nu mai e in lista.
 */
/** "Approve" pe un conector din lista (K4): raspunsurile lui semnate se cred. */
export function approveDirectoryConnector(providerId: number, deviceId: number) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${providerId}/connectors/${deviceId}/approve`, {})
}

export function removeDirectoryConnector(providerId: number, deviceId: number) {
  return apiGet<WriteResult>(`/settings/authentication/providers/${providerId}/connectors/${deviceId}`, {
    method: 'DELETE',
  })
}

/** "Preview sync" - aceeasi trecere, fara nicio scriere; `run.preview` spune ce s-ar schimba. */
export function previewDirectorySync(providerId: number) {
  return apiPost<WriteResult & { run?: SyncRun }>(`/settings/authentication/providers/${providerId}/sync?dry=1`, {
    dry: 1,
  })
}

/** "Create group rules from our roles" - o propunere, nescrisa. */
export function proposeGroupRules(providerId: number, prefix: string) {
  return apiPost<ProposedRules>(`/settings/authentication/providers/${providerId}/groups/propose`, { prefix })
}

/** Previzualizarea unui CSV `group_value;role;companies;order`, nescrisa. */
export function previewGroupRulesCsv(providerId: number, csv: string) {
  return apiPostJson<ProposedRules>(`/settings/authentication/providers/${providerId}/groups/import/preview`, { csv })
}

/** Salveaza setul de reguli vazut de om (propunerea sau importul). */
export function importGroupRules(providerId: number, rows: ProposedRule[]) {
  return apiPostJson<ImportResult>(`/settings/authentication/providers/${providerId}/groups/import`, {
    rows: rows.map((row) => ({
      group_value: row.group_value,
      role_id: row.role_id,
      all_companies: row.all_companies ? 1 : 0,
      company_ids: row.company_ids,
      sort_order: row.sort_order,
    })),
  })
}

export function removeGroupRule(providerId: number, ruleId: number) {
  return apiPost<WriteResult>(`/settings/authentication/providers/${providerId}/groups/${ruleId}/remove`, {})
}

/**
 * Nivelurile de acces, in ordinea din macheta veche (`#authAccess`).
 *
 * Catalogul e primul, nu Administrator: dintr-o alegere nefacuta trebuie sa iasa
 * putinul. Macheta il avea scris de doua ori - o data la inceput si o data la
 * sfarsit - si a doua oara era o scapare, nu o optiune.
 */
export const ACCESS_LEVELS: Array<{ value: number; label: string }> = [
  { value: 6, label: 'IT Service Catalog' },
  { value: 2, label: 'Super Admin' },
  { value: 1, label: 'Admin' },
  /* Fara Super Admin (4) si Service Provider (3): nu se dau automat conturilor
     noi (ProviderFields::NOT_BY_PROVISIONING, M6 din auditul IAM). */
  { value: 5, label: 'GPS Dispatcher' },
]
